import React from "react";
import { Link } from "react-router-dom";
import HubIcon from "./HubIcon";

/**
 * Renders one flat list of nav links (a nav "item"'s children) as a card grid.
 *
 * Existing functionality preserved:
 * - Owner hidden filtering
 * - Portal permission filtering
 * - Portal basePath handling
 * - Empty state fallback
 * - React Router navigation
 * - Responsive Tailwind grid
 */
const HubGrid = ({
  links = [],
  isPortal = false,
  basePath = "/staff-panel",
  hasPermission = () => true,
  emptyFallback = null,
}) => {
  const visibleLinks = links.filter((link) => {
    // Owner view:
    // Preserve existing behavior exactly.
    if (!isPortal) {
      return !link.ownerHidden;
    }

    // Portal view:
    // Links without a permission are not displayed.
    if (link.permission === null || link.permission === undefined) {
      return false;
    }

    return typeof hasPermission === "function"
      ? hasPermission(link.permission)
      : true;
  });

  // Preserve existing empty-state behavior.
  if (visibleLinks.length === 0) {
    return emptyFallback ? (
      <p className="text-sm text-gray-400 dark:text-neutral-500 italic">{emptyFallback}</p>
    ) : null;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {visibleLinks.map((link) => {
        const href = isPortal ? `${basePath}${link.url}` : link.url;

        return (
          <Link
            key={href}
            to={href}
            className="flex items-start gap-4 p-5 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-xl shadow-sm hover:shadow-md hover:border-indigo-200 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            {/* Reusable premium Quiet Duotone icon */}
            {link.icon && (
              <HubIcon
                icon={link.icon}
                size="md"
              />
            )}

            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-neutral-100">
                {link.label}
              </h3>

              {link.desc && (
                <p className="text-xs text-gray-500 dark:text-neutral-400 mt-1">
                  {link.desc}
                </p>
              )}
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export default HubGrid;